import {jsx} from 'panel';

import {SelectTheme} from 'common/components/select/constants';
import {SelectEl} from 'common/components/select';
import {isCohortSegment, SegmentValue} from 'common/query-builder/query-block/segment-menu/types';
import {
  isDateRangeProfileHistoryOptions,
  ProfileHistoryType,
  ProfileHistoryTypeToDisplayName,
  Segmentation,
} from 'common/types/reports/query';
import {ButtonSize, ButtonTheme} from 'common/components/button/constants';
import {SelectAttrs} from 'common/components/select/types';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {QueryBlockEl} from 'common/query-builder/query-block';
import {MenuType} from 'common/query-builder/query-block/types';

import {ControlBarAction, ControlBarActionItems} from '../constants';
import {SelectorTypeDefinitions} from '../types';

/**
 * Renders profile history controls for a given Segmentation entry on filters or breakdowns
 */
export function ProfileHistorySubItem({
  value,
  onTypeChange,
  hasNewUI,
  hideEvents,
  selectorType,
}: {
  value: SegmentValue;
  onTypeChange: (newValue: SegmentValue) => void;
  hasNewUI: boolean;
  hideEvents: boolean;
  selectorType: SelectorTypeDefinitions;
}) {
  if (
    !value ||
    isCohortSegment(value) ||
    ![SelectorTypeDefinitions.Filter, SelectorTypeDefinitions.Segment].includes(selectorType)
  ) {
    return null;
  }

  const segmentation = value as Segmentation;
  const currentType = segmentation.profileHistoryOptions?.type ?? ProfileHistoryType.Latest;

  // Render a date selector to specify the time range
  if (isDateRangeProfileHistoryOptions(segmentation.profileHistoryOptions)) {
    return (
      <div sel='.subitem-text'>
        <QueryBlockEl
          sel='.subitem-style'
          attrs={{
            type: MenuType.Time,
            theme: hasNewUI ? ButtonTheme.QbSubitem : ButtonTheme.Select,
            'button-size': hasNewUI ? ButtonSize.Small : ButtonSize.Medium,
            selected: segmentation.profileHistoryOptions.dateRange,
            'selector-icon': `calendar`,
          }}
          on={{
            select: (ev: CustomEvent<DateRange>) => {
              onTypeChange({
                ...segmentation,
                profileHistoryOptions: {
                  type: ProfileHistoryType.DateRange,
                  dateRange: ev.detail.getAttributes(),
                },
              });
            },
          }}
        />
      </div>
    );
  }

  const displayName = ProfileHistoryTypeToDisplayName[currentType];
  let displaySpecificAttrs: Partial<SelectAttrs>;
  if (hasNewUI) {
    displaySpecificAttrs = {
      theme: SelectTheme.QbSubitem,
      label: `Profile history type changed to **${displayName}**`,
      size: ButtonSize.Small,
    };
  } else {
    displaySpecificAttrs = {
      theme: SelectTheme.Select,
      label: displayName,
    };
  }

  const showMenu = hideEvents
    ? currentType !== ProfileHistoryType.Latest
    : currentType !== ProfileHistoryType.EventTime;

  return (
    <>
      {showMenu && (
        <div sel='.subitem-text'>
          <SelectEl
            sel='.subitem-select'
            attrs={{
              ...displaySpecificAttrs,
              'menu-width': null,
              'never-show-caret': true,
              elref: `profileHistorySubitem`,
            }}
            props={{
              sections: ControlBarActionItems[ControlBarAction.ProfileHistory]({
                currentProfileHistoryType: currentType,
                hideEvents,
                selectorType,
              }).subSections,
            }}
            on={{
              change: (ev: CustomEvent) => {
                onTypeChange({...segmentation, profileHistoryOptions: {type: ev.detail.item.value.toType}});
              },
            }}
          />
        </div>
      )}
    </>
  );
}
